const WS_URL = process.env.REACT_APP_WS_URL || 'ws://localhost:8000';

class WebSocketService {
  constructor() {
    this.socket = null;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.eventHandlers = new Map();
  }

  connect(projectId) {
    try {
      const wsUrl = `${WS_URL}/ws/${projectId}`;
      this.socket = new WebSocket(wsUrl);

      this.socket.onopen = () => {
        console.log('WebSocket connected');
        this.reconnectAttempts = 0;
        this.triggerEvent('connect');
      };

      this.socket.onclose = () => {
        console.log('WebSocket disconnected');
        this.triggerEvent('disconnect');
        this.handleReconnect(projectId);
      };

      this.socket.onerror = (error) => {
        console.error('WebSocket connection error:', error);
        this.triggerEvent('connect_error', error);
        this.handleReconnect(projectId);
      };

      this.socket.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          // For messages like { "type": "idea_updated", "idea": {...} }
          // We want to pass just the message without the type
          const messageType = message.type;
          delete message.type; // Remove type, leaving just the data properties
          this.triggerEvent(messageType, message);
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };

      return this.socket;
    } catch (error) {
      console.error('Failed to connect WebSocket:', error);
      return null;
    }
  }

  handleReconnect(projectId) {
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++;
      console.log(`Attempting to reconnect... (${this.reconnectAttempts}/${this.maxReconnectAttempts})`);
      
      setTimeout(() => {
        this.connect(projectId);
      }, 2000 * this.reconnectAttempts);
    } else {
      console.error('Max reconnection attempts reached');
    }
  }

  disconnect() {
    if (this.socket) {
      this.socket.close();
      this.socket = null;
    }
    this.reconnectAttempts = 0;
    this.eventHandlers.clear();
  }

  triggerEvent(event, data) {
    const handlers = this.eventHandlers.get(event);
    if (handlers) {
      handlers.forEach(handler => {
        try {
          handler(data);
        } catch (error) {
          console.error(`Error in event handler for ${event}:`, error);
        }
      });
    }
  }

  on(event, callback) {
    if (!this.eventHandlers.has(event)) {
      this.eventHandlers.set(event, []);
    }
    this.eventHandlers.get(event).push(callback);
  }

  off(event, callback) {
    const handlers = this.eventHandlers.get(event);
    if (handlers) {
      const index = handlers.indexOf(callback);
      if (index > -1) {
        handlers.splice(index, 1);
      }
    }
  }

  emit(event, data) {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      const message = {
        type: event,
        data: data
      };
      this.socket.send(JSON.stringify(message));
    } else {
      console.warn('WebSocket not connected, cannot emit event:', event);
    }
  }

  // Specific event handlers for brainstorming
  onIdeaCreated(callback) {
    this.on('idea_created', callback);
  }

  onIdeaUpdated(callback) {
    this.on('idea_updated', callback);
  }

  onCommentCreated(callback) {
    this.on('comment_created', callback);
  }

  onConnectionCreated(callback) {
    this.on('connection_created', callback);
  }

  onSessionCreated(callback) {
    this.on('session_created', callback);
  }

  // Send events
  sendMessage(type, data) {
    this.emit('message', {
      type,
      data,
      timestamp: new Date().toISOString(),
    });
  }

  sendIdeaUpdate(idea) {
    this.sendMessage('idea_update', idea);
  }

  sendCursorPosition(position) {
    this.sendMessage('cursor_position', position);
  }

  isConnected() {
    return this.socket && this.socket.readyState === WebSocket.OPEN;
  }
}

export default new WebSocketService();